/* SSi website – Aran's Notebook.
 *
 * Two components for two pages:
 *   AN_Highlights  – 15 curated pieces in 3 clusters, plus books & booklet
 *   AN_Archive     – wider body of work; Nation.Cymru + SSi blog (2013–2019)
 *
 * Design philosophy: a writer's notebook, not a marketing funnel.
 * No badges, no social-proof counts, no "founder voice" framing. The
 * work is the hero. One quiet email signup, near the books at the
 * bottom of the highlights page.
 *
 * All article links open in new tabs – saysomethingin.com is the spine.
 */

import React from 'react';
import { Link } from 'react-router-dom';

/* -------------------------------------------------------------------
 * Highlights data – 5 pieces per cluster.
 * ------------------------------------------------------------------- */

const NC_IMG = '/assets/writing/nation-cymru/';

const HIGHLIGHTS = [
  /* Cluster 1 – How language learning actually works */
  {
    c: 1,
    title: 'Want to Speak Welsh? Make MORE Mistakes (It’s a Superpower)',
    url: 'https://nation.cymru/feature/want-to-speak-welsh-make-more-mistakes-its-a-superpower/',
    src: 'Nation.Cymru',
    min: 5,
    img: NC_IMG + 'want-to-speak-welsh-make-more-mistakes-its-a-superpower.jpeg',
    excerpt: 'Why mistakes aren’t the obstacle to learning – they’re the engine. The shortest, sharpest statement of the SaySomethingin philosophy.',
  },
  {
    c: 1,
    title: 'How Happiness Helps You Learn Welsh (Not the Other Way Round)',
    url: 'https://nation.cymru/news/how-happiness-helps-you-learn-welsh-not-the-other-way-round/',
    src: 'Nation.Cymru',
    min: 6,
    img: NC_IMG + 'how-happiness-helps-you-learn-welsh-not-the-other-way-round.jpg',
    excerpt: 'On the intersection of mindfulness practice and language method. Happiness comes first; the learning follows.',
  },
  {
    c: 1,
    title: 'What Learning Welsh Reveals About the Power of the Brain',
    url: 'https://nation.cymru/feature/what-learning-welsh-reveals-about-the-power-of-the-brain/',
    src: 'Nation.Cymru',
    min: 6,
    img: NC_IMG + 'what-learning-welsh-reveals-about-the-power-of-the-brain.png',
    excerpt: 'Neuroplasticity, dementia research, and what learning a new language actually does to the adult brain – through Aran’s own intellectual journey with Cardiff University.',
  },
  {
    c: 1,
    title: 'Zen and the Art of Language Learning',
    url: 'https://nation.cymru/feature/zen-and-the-art-of-language-learning/',
    src: 'Nation.Cymru',
    min: 5,
    img: NC_IMG + 'zen-and-the-art-of-language-learning.jpg',
    excerpt: 'A preview of the forthcoming book of the same name. The philosophy and practice that runs underneath everything.',
  },
  {
    c: 1,
    title: 'Practice and the Art of Learning Welsh',
    url: 'https://nation.cymru/feature/practice-and-the-art-of-learning-welsh/',
    src: 'Nation.Cymru',
    min: 5,
    img: NC_IMG + 'practice-and-the-art-of-learning-welsh.jpg',
    excerpt: 'Methodology meets philosophy. A companion piece to the happiness article: why the work matters more than the talent.',
  },

  /* Cluster 2 – Why it matters */
  {
    c: 2,
    title: 'Reclaiming Your Own Land – Why Learning Welsh Is Harder for Welsh People',
    url: 'https://nation.cymru/feature/reclaiming-your-own-land-why-learning-welsh-is-harder-for-welsh-people/',
    src: 'Nation.Cymru',
    min: 7,
    img: NC_IMG + 'reclaiming-your-own-land-why-learning-welsh-is-harder-for-welsh-people.jpg',
    excerpt: 'The fierce joy of learning a language you were never given. R.S. Thomas, heritage shame, and the particular weight Welsh carries for Welsh people.',
  },
  {
    c: 2,
    title: 'Why Learning Smaller Languages Is More Rewarding',
    url: 'https://nation.cymru/feature/why-learning-smaller-languages-is-more-rewarding/',
    src: 'Nation.Cymru',
    min: 6,
    img: NC_IMG + 'why-learning-smaller-languages-is-more-rewarding.jpg',
    excerpt: 'Why a conversation in Caernarfon Welsh beats one in Paris French. The case for minority-language learning, without guilt or duty.',
  },
  {
    c: 2,
    title: 'What It Feels Like to Become a Welsh Speaker (Yes You Can)',
    url: 'https://nation.cymru/feature/what-it-feels-like-to-become-a-welsh-speaker-yes-you-can/',
    src: 'Nation.Cymru',
    min: 5,
    img: NC_IMG + 'what-it-feels-like-to-become-a-welsh-speaker-yes-you-can.jpeg',
    excerpt: '“Everything I said in Welsh felt like wine in my mouth.” A personal arc, from years of failure to fluency – written for anyone who thinks they cannot.',
  },
  {
    c: 2,
    title: 'Why Is It a Good Idea to Learn Welsh?',
    url: 'https://nation.cymru/feature/why-is-it-a-good-idea-to-learn-welsh/',
    src: 'Nation.Cymru',
    min: 5,
    img: NC_IMG + 'why-is-it-a-good-idea-to-learn-welsh.jpg',
    excerpt: 'Practical and emotional reasons to learn Welsh – with the Cwm Cynllwyd story, on being woven back into a place.',
  },
  {
    c: 2,
    title: 'Happiness for Revolutionaries',
    url: 'https://nation.cymru/feature/happiness-for-revolutionaries/',
    src: 'Nation.Cymru',
    min: 6,
    img: NC_IMG + 'happiness-for-revolutionaries.jpg',
    excerpt: 'Aran on political activism and Daoist philosophy. Shows the thinking goes much wider than methodology.',
  },

  /* Cluster 3 – Stories from the road */
  {
    c: 3,
    title: 'Aleighcia Scott – The Courage of Learning in Public',
    url: 'https://nation.cymru/opinion/aleighcia-scott-the-courage-of-learning-in-public/',
    src: 'Nation.Cymru',
    min: 4,
    img: NC_IMG + 'aleighcia-scott-the-courage-of-learning-in-public.jpeg',
    excerpt: 'On the courage of learning Welsh in public, the Jamaica connection, and what it costs to face the room when the room is unkind.',
  },
  {
    c: 3,
    title: '24 Hours That Changed Wales – A Learning Journey',
    url: 'https://nation.cymru/feature/24-hours-that-changed-wales-a-learning-journey/',
    src: 'Nation.Cymru',
    min: 7,
    img: NC_IMG + '24-hours-that-changed-wales-a-learning-journey.jpeg',
    excerpt: 'Richard Parks, the South Pole, and the kind of mistakes you learn from. A polar explorer on language learning – and stakes.',
  },
  {
    c: 3,
    title: 'What It’s Like to Give a Welsh Language Class to 7 Million People',
    url: 'https://nation.cymru/opinion/what-its-like-to-give-a-welsh-language-class-to-7-million-people/',
    src: 'Nation.Cymru',
    min: 9,
    img: NC_IMG + 'what-its-like-to-give-a-welsh-language-class-to-7-million-people.jpg',
    excerpt: 'Behind the scenes of the Jeremy Vine Radio 2 moment. Funny, revealing, and honest about what it costs to teach a country live on air.',
  },
  {
    c: 3,
    title: 'Cymru/Japan 2025: Old Languages, New Connections',
    url: 'https://nation.cymru/feature/cymru-japan-2025-old-languages-new-connections/',
    src: 'Nation.Cymru',
    min: 6,
    img: NC_IMG + 'cymru-japan-2025-old-languages-new-connections.jpg',
    excerpt: 'The strange feeling of Japanese coming back to life decades later – and what it means to find connection through someone else’s mother tongue.',
  },
  {
    c: 3,
    title: 'Language Exchange Drives Cultural Change',
    url: 'https://nation.cymru/feature/language-exchange-drives-cultural-change/',
    src: 'Nation.Cymru',
    min: 5,
    img: NC_IMG + 'language-exchange-drives-cultural-change.jpeg',
    excerpt: 'Patricia from Spain learns Welsh after seeing Ioan Gruffudd – and what it tells us about Welsh learning becoming normalised through international learners.',
  },
];

const CLUSTERS = [
  { n: '01', t: 'How language learning actually works', l: 'Methodology, psychology, and the science of why the SaySomethingin Method works.', theme: 'paper' },
  { n: '02', t: 'Why it matters',                        l: 'Identity, belonging, and the case for languages as deep human experience.', theme: 'light' },
  { n: '03', t: 'Stories from the road',                 l: 'Personal narratives, celebrity learners, what the work looks like in real life.', theme: 'dark' },
];

const CLUSTER_THEMES = {
  light: {
    bg: '#fff',
    fg: 'var(--fg-1)',
    sub: 'var(--grey)',
    border: 'var(--border)',
    cardBg: 'var(--offWhite)',
    cardBorder: 'transparent',
    cardHoverBorder: 'rgba(219,30,23,.4)',
    num: 'var(--brandRed)',
    accent: 'var(--brandRed)',
    rule: 'var(--brandRed)',
  },
  paper: {
    bg: 'var(--offWhite)',
    fg: 'var(--fg-1)',
    sub: 'var(--grey)',
    border: 'rgba(15,18,18,.10)',
    cardBg: '#fff',
    cardBorder: 'var(--border)',
    cardHoverBorder: 'rgba(219,30,23,.4)',
    num: 'var(--brandRed)',
    accent: 'var(--brandRed)',
    rule: 'var(--brandRed)',
  },
  dark: {
    bg: 'var(--brandRed)',
    fg: 'var(--offWhite)',
    sub: 'rgba(253,245,245,.78)',
    border: 'rgba(253,245,245,.22)',
    cardBg: 'rgba(255,255,255,.06)',
    cardBorder: 'rgba(253,245,245,.20)',
    cardHoverBorder: 'var(--gold)',
    num: 'var(--gold)',
    accent: 'var(--gold)',
    rule: 'var(--gold)',
  },
};

/* -------------------------------------------------------------------
 * Archive data – Nation.Cymru extras + SSi blog (2013–2019).
 * ------------------------------------------------------------------- */

const ARCHIVE = {
  1: {
    nation: [
      { t: 'Learning Welsh – Neurons vs Emotions', u: 'https://nation.cymru/feature/learning-welsh-neurons-vs-emotions/', img: NC_IMG + 'learning-welsh-neurons-vs-emotions.jpg' },
      { t: 'How Micro-Immersion Can Help You Become a Welsh Speaker', u: 'https://nation.cymru/feature/how-micro-immersion-can-help-you-become-a-welsh-speaker/', img: NC_IMG + 'how-micro-immersion-can-help-you-become-a-welsh-speaker.jpg' },
      { t: 'Are You Good Enough at Learning Languages to Learn Welsh?', u: 'https://nation.cymru/feature/are-you-good-enough-at-learning-languages-to-learn-welsh/', img: NC_IMG + 'are-you-good-enough-at-learning-languages-to-learn-welsh.webp' },
    ],
    series: [
      {
        heading: 'The 7 Stages of Language Learning',
        sub: 'A connected seven-part series · 2015',
        items: [
          { t: 'Stage 1', u: 'https://saysomethingin.wordpress.com/2015/04/03/7-stages-1/' },
          { t: 'Stage 2', u: 'https://saysomethingin.wordpress.com/2015/04/09/7-stages-2/' },
          { t: 'Stage 3', u: 'https://saysomethingin.wordpress.com/2015/04/17/7-stages-3/' },
          { t: 'Stage 4', u: 'https://saysomethingin.wordpress.com/2015/04/23/7-stages-4/' },
          { t: 'Stage 5', u: 'https://saysomethingin.wordpress.com/2015/05/01/7-stages-5/' },
          { t: 'Stage 6', u: 'https://saysomethingin.wordpress.com/2015/05/08/7-stages-6/' },
          { t: 'Stage 7', u: 'https://saysomethingin.wordpress.com/2015/05/21/7-stages-7/' },
        ],
      },
      {
        heading: 'The SaySomethingin Method series',
        sub: '2015',
        items: [
          { t: 'High Intensity Language Training – how to make it work', u: 'https://saysomethingin.wordpress.com/2015/01/15/high-intensity/' },
          { t: 'HILT – when to repeat sessions', u: 'https://saysomethingin.wordpress.com/2015/01/22/high-intensity-language-training-when-to-repeat-sessions/' },
          { t: 'Make It Stick – scientific support for what we do', u: 'https://saysomethingin.wordpress.com/2015/03/20/make-it-stick-a-book-which-uncovers-scientific-support-for-some-of-what-we-do/' },
        ],
      },
      {
        heading: 'The Accelerated Learning experiments',
        sub: '2013 · 2019',
        items: [
          { t: 'Adventures in the Language Jungle', y: 2013, u: 'https://saysomethingin.wordpress.com/2013/07/02/adventures-in-the-language-jungle/' },
          { t: 'Accelerated language learning – what and how we’re testing', y: 2013, u: 'https://saysomethingin.wordpress.com/2013/07/02/accelerated-language-learning-what-and-how-were-testing/' },
          { t: 'Would I be rude enough to tell you that you’re doing it all wrong?', y: 2013, u: 'https://saysomethingin.wordpress.com/2013/07/18/would-i-be-rude-enough-to-tell-you-that-youre-doing-it-all-wrong-oh-yes/' },
          { t: 'A lunatic’s guide to building a language course', y: 2013, u: 'https://saysomethingin.wordpress.com/2013/10/23/from-confusion-to-construction-a-lunatics-guide-to-building-a-language-course/' },
          { t: 'How long does it take to learn Welsh?', y: 2019, u: 'https://saysomethingin.wordpress.com/2019/01/02/how-long-does-it-take-to-learn-welsh/' },
        ],
      },
    ],
  },
  2: {
    nation: [
      { t: 'How Does Learning Welsh Help With Other Languages?', u: 'https://nation.cymru/feature/how-does-learning-welsh-help-with-other-languages/', img: NC_IMG + 'how-does-learning-welsh-help-with-other-languages.jpg' },
      { t: 'Every Single Word Makes a Difference – Or Why Learners Matter', u: 'https://nation.cymru/feature/every-single-word-makes-a-difference-or-why-learners-matter/', img: NC_IMG + 'every-single-word-makes-a-difference-or-why-learners-matter.jpg' },
      { t: 'Elections in Wales – Finding Out Who We Are', u: 'https://nation.cymru/opinion/elections-in-wales-finding-out-who-we-are/', img: NC_IMG + 'elections-in-wales-finding-out-who-we-are.jpg' },
      { t: 'Book Review: The Listening Society by Hanzi Freinacht', u: 'https://nation.cymru/culture/book-review-the-listening-society-by-hanzi-freinacht/', img: NC_IMG + 'book-review-the-listening-society-by-hanzi-freinacht.jpg' },
    ],
    series: [
      {
        heading: 'Earlier reflections',
        sub: '2013 · 2015 · 2019',
        items: [
          { t: 'Making a Fool of Yourself: How to get started', y: 2015, u: 'https://saysomethingin.wordpress.com/2015/01/08/making-a-fool/' },
          { t: 'How angry schools can make me', y: 2013, u: 'https://saysomethingin.wordpress.com/2013/10/31/the-loveliness-of-learners-and-how-angry-schools-can-make-me/' },
          { t: 'The value of music to Welsh learners', y: 2013, u: 'https://saysomethingin.wordpress.com/2013/12/09/the-value-of-music-to-welsh-learners-and-the-potential-value-of-learners-to-welsh-music/' },
          { t: 'The international language of Welsh – hint, we’re going to win this', y: 2019, u: 'https://saysomethingin.wordpress.com/2019/02/18/the-international-language-of-welsh-hint-were-going-to-win-this/' },
          { t: 'The English, eh? It’s all THEIR fault', y: 2019, u: 'https://saysomethingin.wordpress.com/2019/02/08/the-english-eh-its-all-their-fault/' },
          { t: 'So why are English people interested in learning Welsh?', y: 2019, u: 'https://saysomethingin.wordpress.com/2019/02/11/so-why-are-english-people-interested-in-learning-welsh/' },
        ],
      },
    ],
  },
  3: {
    nation: [
      { t: 'Jeremy Vine to Make Amends with 10-Minute Welsh Lesson', u: 'https://nation.cymru/news/jeremy-vine-to-make-amends-for-tweet-with-10-minute-welsh-language-lesson-on-st-davids-show/', img: NC_IMG + 'jeremy-vine-to-make-amends-for-tweet-with-10-minute-welsh-language-lesson-on-st-davids-show.jpg' },
      { t: 'Why We Shouldn’t Shout at Jeremy Vine', u: 'https://nation.cymru/opinion/why-we-shouldnt-shout-at-jeremy-vine/', img: NC_IMG + 'why-we-shouldnt-shout-at-jeremy-vine.jpg' },
      { t: 'Japanuary: Ten Day Challenge to Learn Japanese', u: 'https://nation.cymru/feature/japanuary-ten-day-challenge-to-learn-japanese-launched-in-wales/', img: NC_IMG + 'japanuary-ten-day-challenge-to-learn-japanese-launched-in-wales.jpg' },
      { t: 'Irish Language Learning Course Launched', u: 'https://nation.cymru/feature/irish-language-learning-course-launched-by-welsh-innovators/', img: NC_IMG + 'irish-language-learning-course-launched-by-welsh-innovators.jpg' },
      { t: 'Sri Lankan Teachers Speaking Welsh After One Hour', u: 'https://nation.cymru/culture/watch-extraordinary-video-of-sri-lankan-teachers-speaking-welsh-after-one-hour/', img: NC_IMG + 'watch-extraordinary-video-of-sri-lankan-teachers-speaking-welsh-after-one-hour.jpg' },
    ],
    series: [
      {
        heading: 'The Welsh Class of 7 Million series',
        sub: 'Jeremy Vine on BBC Radio 2 · 2019',
        items: [
          { t: 'Part 1 – What did a Welsh class of 7 million look like from the teacher’s seat?', u: 'https://saysomethingin.wordpress.com/2019/03/11/what-did-a-welsh-class-of-7-million-look-like-from-the-teachers-seat/' },
          { t: 'Part 2', u: 'https://saysomethingin.wordpress.com/2019/03/12/a-welsh-class-of-7-million-part-2/' },
          { t: 'Part 3', u: 'https://saysomethingin.wordpress.com/2019/03/13/a-welsh-class-of-7-million-part-3/' },
          { t: 'How a SINGLE sentence in Welsh can change attitudes', u: 'https://saysomethingin.wordpress.com/2019/03/06/how-a-single-sentence-in-welsh-can-change-attitudes/' },
          { t: 'Is live radio the best environment for teaching Welsh?', u: 'https://saysomethingin.wordpress.com/2019/02/27/is-live-radio-the-best-environment-for-teaching-welsh/' },
        ],
      },
      {
        heading: 'Welsh in America',
        sub: '2019',
        items: [
          { t: 'I had NO idea Americans were so serious about Welsh', u: 'https://saysomethingin.wordpress.com/2019/02/14/i-had-no-idea-americans-were-so-serious-about-welsh/' },
          { t: 'Becoming American and belonging to Wales', u: 'https://saysomethingin.wordpress.com/2019/02/15/becoming-american-and-belonging-to-wales/' },
          { t: 'Gateway drugs to the world of multinational Welsh', u: 'https://saysomethingin.wordpress.com/2019/02/19/gateway-drugs-to-the-world-of-multinational-welsh/' },
        ],
      },
      {
        heading: 'Other reflections',
        sub: '2013 · 2019',
        items: [
          { t: 'Secretly terrified of Welsh?', y: 2019, u: 'https://saysomethingin.wordpress.com/2019/02/12/secretly-terrified-of-welsh/' },
          { t: 'Very occasionally, people are downright nasty to Welsh learners', y: 2019, u: 'https://saysomethingin.wordpress.com/2019/02/13/very-occasionally-people-are-downright-nasty-to-welsh-learners/' },
          { t: 'How a small group of volunteers changed the future of a project', y: 2013, u: 'https://saysomethingin.wordpress.com/2013/12/14/volunteers/' },
        ],
      },
    ],
  },
};

/* -------------------------------------------------------------------
 * Highlights page
 * ------------------------------------------------------------------- */

function AN_Highlights() {
  return (
    <main data-screen-label="Aran's Notebook">
      <AN_Hero/>
      <AN_ByLine/>
      {CLUSTERS.map(cl => (
        <AN_Cluster
          key={cl.n}
          cluster={cl}
          items={HIGHLIGHTS.filter(h => h.c === Number(cl.n))}
        />
      ))}
      <AN_Books/>
      <AN_ArchiveLink/>
    </main>
  );
}

/* Hero – portrait on the left, opening line on the right. */
function AN_Hero() {
  return (
    <section style={anStyles.heroWrap} data-screen-label="Hero">
      <div style={anStyles.heroInner} className="an-hero">
        <div style={anStyles.heroPortraitWrap} className="an-hero-portrait">
          <img
            src="/assets/writing/aran-portrait.jpg"
            alt="Aran Jones"
            style={anStyles.heroPortrait}
          />
        </div>
        <div style={anStyles.heroCopy}>
          <div style={anStyles.heroKicker}>Aran’s Notebook</div>
          <blockquote style={anStyles.heroQuote}>
            “Everything I said in Welsh felt like wine in my mouth.”
          </blockquote>
          <div style={anStyles.heroAttr}>
            – from <em>Some Sex and a Hill, or How to Learn Welsh in Three Easy Pints</em>
          </div>
        </div>
      </div>
    </section>
  );
}

/* Byline – name, role, intro paragraph. */
function AN_ByLine() {
  return (
    <section style={anStyles.byWrap} data-screen-label="Byline">
      <div style={anStyles.byInner}>
        <div style={anStyles.byCopy}>
          <div style={anStyles.byName}>Aran Jones</div>
          <div style={anStyles.byRole}>Founder, SaySomethingin</div>
          <p style={anStyles.byIntro}>
            I’ve been scribbling away about learning languages for donkey’s years now. Here are some of the pieces that people have told me they liked – the ones that no one said anything nice about are hidden away in the archive. You might be better off not bothering with those, but there’s a link to them at the bottom anyway.
          </p>
        </div>
      </div>
    </section>
  );
}

/* One cluster of highlight cards. */
function AN_Cluster({ cluster, items }) {
  const theme = CLUSTER_THEMES[cluster.theme || 'light'];
  const wrapStyle = {
    ...anStyles.clusterWrap,
    background: theme.bg,
    color: theme.fg,
    '--cl-card-bg': theme.cardBg,
    '--cl-card-border': theme.cardBorder,
    '--cl-card-hover-border': theme.cardHoverBorder,
    '--cl-accent': theme.accent,
    '--cl-fg': theme.fg,
    '--cl-sub': theme.sub,
  };
  const headStyle = { ...anStyles.clusterHead, borderColor: theme.border };
  const numStyle  = { ...anStyles.clusterN,    color: theme.num };
  const h2Style   = { ...anStyles.clusterH2,   color: theme.fg };
  const ledeStyle = { ...anStyles.clusterL,    color: theme.sub };
  const ruleStyle = { ...anStyles.clusterRule, background: theme.rule };
  const [featured, ...rest] = items;
  return (
    <section style={wrapStyle} data-screen-label={`Cluster ${cluster.n}`} className={`an-cluster an-cluster--${cluster.theme || 'light'}`}>
      <div style={anStyles.clusterInner}>
        <header style={headStyle} className="an-cluster-head">
          <div style={numStyle}>{cluster.n}</div>
          <div style={anStyles.clusterHeadCopy}>
            <span style={ruleStyle} aria-hidden="true"/>
            <h2 style={h2Style}>{cluster.t}</h2>
            <p style={ledeStyle}>{cluster.l}</p>
          </div>
        </header>
        {featured && <AN_FeaturedCard item={featured}/>}
        <ul style={anStyles.cardList} className="an-card-list">
          {rest.map(it => <AN_Card key={it.url} item={it}/>)}
        </ul>
      </div>
    </section>
  );
}

/* Featured (first) card per cluster – full-width, image on right, larger title. */
function AN_FeaturedCard({ item }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      style={anStyles.featCard}
      className="an-feat an-card"
    >
      <div style={anStyles.featLeft}>
        <div style={anStyles.featTag}>Featured</div>
        <div style={anStyles.featMeta}>
          <span style={anStyles.cardSrc}>{item.src}</span>
          <span style={anStyles.cardDot}>·</span>
          <span style={anStyles.cardMin}>{item.min} min read</span>
        </div>
        <h3 style={anStyles.featH3}>{item.title}</h3>
        <p style={anStyles.featExcerpt}>{item.excerpt}</p>
        <span style={anStyles.featArrow} className="an-arrow" aria-hidden="true">
          Read on {item.src}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft:10}}>
            <path d="M7 17L17 7"/>
            <path d="M8 7h9v9"/>
          </svg>
        </span>
      </div>
      {item.img && (
        <div style={anStyles.featImgWrap} className="an-feat-img">
          <img src={item.img} alt="" loading="lazy" style={anStyles.featImg}/>
        </div>
      )}
    </a>
  );
}

/* Single article card. */
function AN_Card({ item }) {
  return (
    <li style={anStyles.card}>
      <a href={item.url} target="_blank" rel="noopener noreferrer" style={anStyles.cardA} className="an-card">
        {item.img && (
          <div style={anStyles.cardImgWrap} className="an-card-img">
            <img src={item.img} alt="" loading="lazy" style={anStyles.cardImg}/>
          </div>
        )}
        <div style={anStyles.cardBody}>
          <div style={anStyles.cardMeta}>
            <span style={anStyles.cardSrc}>{item.src}</span>
            <span style={anStyles.cardDot}>·</span>
            <span style={anStyles.cardMin}>{item.min} min read</span>
          </div>
          <h3 style={anStyles.cardH3}>{item.title}</h3>
          <p style={anStyles.cardP}>{item.excerpt}</p>
          <span style={anStyles.cardArrow} className="an-arrow" aria-hidden="true">
            Read on {item.src}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft:8}}>
              <path d="M7 17L17 7"/>
              <path d="M8 7h9v9"/>
            </svg>
          </span>
        </div>
      </a>
    </li>
  );
}

/* Things Aran has written – books & booklet. */
function AN_Books() {
  return (
    <section style={anStyles.booksWrap} data-screen-label="Things Aran has written">
      <div style={anStyles.booksInner}>
        <div style={anStyles.booksKicker}>Things Aran has written</div>
        <h2 style={anStyles.booksH2}>Three books, one of them still in progress.</h2>

        <div style={anStyles.booksGrid} className="an-books-grid">
          {/* HILT booklet */}
          <article style={anStyles.bookCard}>
            <div style={anStyles.bookCoverWrap}>
              <img src="/assets/writing/hilt-cover.jpg" alt="High Intensity Language Training – cover" style={anStyles.bookCoverImg}/>
              <span style={anStyles.bookBadge}>Free booklet</span>
            </div>
            <div style={anStyles.bookBody}>
              <h3 style={anStyles.bookH3}>High Intensity Language Training</h3>
              <div style={anStyles.bookSub}>An introduction to the key principles</div>
              <p style={anStyles.bookP}>The foundational methodology guide behind SaySomethingin. Eleven short chapters covering why traditional language learning fails, how the brain actually acquires new languages, and the four core principles of HILT in practice.</p>
              <div style={anStyles.bookCtas}>
                <a href="/assets/writing/hilt-introduction.pdf" download style={anStyles.bookCtaPrimary}>
                  Download the PDF
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft:8}}>
                    <path d="M12 3v12"/><path d="M7 10l5 5 5-5"/><path d="M5 21h14"/>
                  </svg>
                </a>
                <a href="https://www.amazon.co.uk/High-Intensity-Language-Training-introduction-ebook/dp/B00PISI0EM" target="_blank" rel="noopener noreferrer" style={anStyles.bookCtaSecondary}>Or read on Kindle</a>
              </div>
            </div>
          </article>

          {/* Some Sex and a Hill */}
          <article style={anStyles.bookCard}>
            <div style={anStyles.bookCoverWrap}>
              <img src="/assets/writing/ssaah-cover.jpg" alt="Some Sex and a Hill – cover" style={anStyles.bookCoverImg}/>
              <span style={anStyles.bookBadge}>2015</span>
            </div>
            <div style={anStyles.bookBody}>
              <h3 style={anStyles.bookH3}>Some Sex and a Hill</h3>
              <div style={anStyles.bookSub}>A Story of How to Learn Welsh</div>
              <p style={anStyles.bookP}>A light-hearted account of Aran’s own journey learning Welsh – the failures, the breakthroughs, and the unlikely moments that turned a Welsh man brought up overseas without the language into a confident Welsh speaker.</p>
              <div style={anStyles.bookCtas}>
                <a href="https://www.amazon.co.uk/Some-Sex-Hill-Learn-Welsh/dp/1519058519" target="_blank" rel="noopener noreferrer" style={anStyles.bookCtaPrimary}>
                  Buy on Amazon
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft:8}}>
                    <path d="M7 17L17 7"/><path d="M8 7h9v9"/>
                  </svg>
                </a>
              </div>
            </div>
          </article>

          {/* Zen and the Art – forthcoming, email signup */}
          <article style={{...anStyles.bookCard, ...anStyles.bookCardForth}}>
            <div style={{...anStyles.bookCoverWrap, ...anStyles.bookCoverForth}}>
              <div style={anStyles.bookCoverPlaceholder}>
                <div style={anStyles.bookCoverPlaceTitle}>Zen and the Art<br/>of Language Learning</div>
                <div style={anStyles.bookCoverPlaceSub}>Aran Jones</div>
              </div>
              <span style={{...anStyles.bookBadge, ...anStyles.bookBadgeForth}}>Forthcoming</span>
            </div>
            <div style={anStyles.bookBody}>
              <h3 style={anStyles.bookH3}>Zen and the Art of Language Learning</h3>
              <div style={anStyles.bookSub}>In progress</div>
              <p style={anStyles.bookP}>The ways in which learning a language and studying Zen overlap and strengthen each other, and the fascinating paths that can take you on as a language learner and a human being. Currently being carved out of Aran's beating heart one painful word at a time, expected some time before the end of the decade.</p>
              <AN_LaunchSignup/>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

/* Quiet email signup – the one place email capture sits naturally. */
function AN_LaunchSignup() {
  const [email, setEmail] = React.useState('');
  const [sent, setSent] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const onSubmit = async (e) => {
    e.preventDefault();
    if (e.target.elements.website && e.target.elements.website.value) return; // honeypot
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_BASE}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form: 'notebook', email }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSent(true);
    } catch {
      setError('Something went wrong — please try again, or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };
  if (sent) {
    return (
      <div style={anStyles.signupSent}>
        Thanks. We’ll be in touch when the book is ready.
      </div>
    );
  }
  return (
    <form onSubmit={onSubmit} style={anStyles.signupForm} aria-label="Sign up to hear when the book launches">
      <div aria-hidden="true" style={{position:'absolute', left:'-10000px', width:1, height:1, overflow:'hidden'}}>
        <label>Website (leave empty)<input type="text" name="website" tabIndex={-1} autoComplete="off"/></label>
      </div>
      <label style={anStyles.signupLabel}>Tell me when it’s ready</label>
      <div style={anStyles.signupRow} className="an-signup-row">
        <input
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          style={anStyles.signupInput}
          disabled={submitting}
        />
        <button type="submit" style={anStyles.signupBtn} disabled={submitting}>
          {submitting ? '…' : 'Yes!'}
        </button>
      </div>
      {error && <div style={anStyles.signupError}>{error}</div>}
    </form>
  );
}

/* Link out to the fuller archive at the very bottom. */
function AN_ArchiveLink() {
  return (
    <section style={anStyles.archLinkWrap} data-screen-label="Archive link">
      <div style={anStyles.archLinkInner}>
        <Link to="/arans-notebook-archive" style={anStyles.archLinkA}>
          <span style={anStyles.archLinkKick}>Want more?</span>
          <span style={anStyles.archLinkH}>The full archive is here</span>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft:12}}>
            <path d="M5 12h14"/><path d="M13 5l7 7-7 7"/>
          </svg>
        </Link>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------
 * Archive page
 * ------------------------------------------------------------------- */

function AN_Archive() {
  return (
    <main data-screen-label="Aran's Notebook · Archive">
      <section style={anStyles.archHeroWrap} data-screen-label="Hero">
        <div style={anStyles.archHeroInner}>
          <Link to="/arans-notebook" style={anStyles.archCrumb}>← Aran’s Notebook</Link>
          <h1 style={anStyles.archH1}>The fuller archive</h1>
          <p style={anStyles.archLede}>
            The wider body of Aran’s writing, organised by theme. <em>Nation.Cymru</em> articles open at Nation.Cymru; older pieces from the SSi blog open at their original location on WordPress. Both will open in new tabs.
          </p>
        </div>
      </section>

      {CLUSTERS.map(cl => (
        <AN_ArchiveCluster
          key={cl.n}
          cluster={cl}
          data={ARCHIVE[Number(cl.n)]}
        />
      ))}
    </main>
  );
}

function AN_ArchiveCluster({ cluster, data }) {
  if (!data) return null;
  return (
    <section style={anStyles.archClusterWrap} data-screen-label={`Cluster ${cluster.n}`}>
      <div style={anStyles.archClusterInner}>
        <header style={anStyles.clusterHead}>
          <div style={anStyles.clusterN}>{cluster.n}</div>
          <div>
            <h2 style={anStyles.clusterH2}>{cluster.t}</h2>
            <p style={anStyles.clusterL}>{cluster.l}</p>
          </div>
        </header>

        {data.nation && data.nation.length > 0 && (
          <div style={anStyles.archGroup}>
            <h3 style={anStyles.archGroupH3}>Nation.Cymru articles</h3>
            <ul style={anStyles.archList} className="an-arch-list">
              {data.nation.map(it => (
                <li key={it.u} style={anStyles.archItem}>
                  <a href={it.u} target="_blank" rel="noopener noreferrer" style={anStyles.archA} className="an-arch-a">
                    {it.img && <img src={it.img} alt="" loading="lazy" style={anStyles.archThumb}/>}
                    <span style={anStyles.archTitle}>{it.t}</span>
                    <span style={anStyles.archExt} aria-hidden="true">↗</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(data.series || []).map(group => (
          <div key={group.heading} style={anStyles.archGroup}>
            <h3 style={anStyles.archGroupH3}>{group.heading}</h3>
            {group.sub && <div style={anStyles.archGroupSub}>{group.sub}</div>}
            <ul style={anStyles.archList} className="an-arch-list">
              {group.items.map(it => (
                <li key={it.u} style={anStyles.archItem}>
                  <a href={it.u} target="_blank" rel="noopener noreferrer" style={anStyles.archA} className="an-arch-a">
                    {it.img && <img src={it.img} alt="" loading="lazy" style={anStyles.archThumb}/>}
                    <span style={anStyles.archTitle}>{it.t}</span>
                    {it.y && <span style={anStyles.archYear}>({it.y})</span>}
                    <span style={anStyles.archExt} aria-hidden="true">↗</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------
 * Styles
 * ------------------------------------------------------------------- */

const anStyles = {
  /* Hero */
  heroWrap: { background: 'var(--offWhite)', padding: '88px 32px 80px', borderBottom: '1px solid var(--border)' },
  heroInner: { maxWidth: 1180, margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(280px, 420px) 1fr', gap: 64, alignItems: 'center' },
  heroPortraitWrap: { borderRadius: 12, overflow: 'hidden', background: 'var(--offWhite)', aspectRatio: '1 / 1', boxShadow: '0 18px 40px rgba(0,0,0,.10), 0 4px 12px rgba(0,0,0,.06)' },
  heroPortrait: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  heroCopy: { minWidth: 0 },
  heroKicker: { fontSize: 12, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--brandRed)', fontWeight: 700, marginBottom: 28 },
  heroQuote: { fontFamily: 'var(--font-display)', fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.1, fontWeight: 700, fontStyle: 'italic', letterSpacing: '-.02em', margin: '0 0 22px', textWrap: 'balance' },
  heroAttr: { fontSize: 15, color: 'var(--grey)', lineHeight: 1.6 },

  /* Byline */
  byWrap: { background: '#fff', padding: '64px 32px 32px' },
  byInner: { maxWidth: 720, margin: '0 auto' },
  byCopy: { minWidth: 0 },
  byName: { fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--fg-1)' },
  byRole: { fontSize: 13, color: 'var(--grey)', marginBottom: 14 },
  byIntro: { fontSize: 18, lineHeight: 1.65, color: 'var(--fg-1)', margin: 0, textWrap: 'pretty' },

  /* Cluster */
  clusterWrap: { padding: '88px 32px 88px', transition: 'background .2s var(--ease-out)' },
  clusterInner: { maxWidth: 1100, margin: '0 auto' },
  clusterHead: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 36, alignItems: 'start', marginBottom: 48, paddingBottom: 36, borderBottom: '1px solid var(--border)' },
  clusterHeadCopy: { paddingTop: 10, minWidth: 0 },
  clusterRule: { display: 'block', width: 56, height: 3, background: 'var(--brandRed)', marginBottom: 18, borderRadius: 2 },
  clusterN: { fontFamily: 'var(--font-display)', fontSize: 'clamp(80px, 11vw, 132px)', fontWeight: 700, color: 'var(--brandRed)', lineHeight: .82, letterSpacing: '-.045em', fontVariantNumeric: 'lining-nums' },
  clusterH2: { fontFamily: 'var(--font-display)', fontSize: 'clamp(30px, 3.6vw, 42px)', lineHeight: 1.05, fontWeight: 700, letterSpacing: '-.02em', margin: '0 0 12px', textWrap: 'balance' },
  clusterL: { fontSize: 17, lineHeight: 1.55, color: 'var(--grey)', margin: 0, textWrap: 'pretty', maxWidth: '54ch' },

  /* Featured card */
  featCard: { display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 40, alignItems: 'stretch', padding: 18, borderRadius: 18, background: 'var(--cl-card-bg, #fff)', border: '1px solid var(--cl-card-border, var(--border))', color: 'var(--cl-fg, var(--fg-1))', textDecoration: 'none', marginBottom: 24, transition: 'transform .18s var(--ease-out), box-shadow .18s var(--ease-out), border-color .18s var(--ease-out)' },
  featLeft: { display: 'flex', flexDirection: 'column', minWidth: 0, padding: '20px 8px 16px 24px' },
  featTag: { display: 'inline-flex', alignSelf: 'flex-start', fontSize: 11, letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--cl-accent, var(--brandRed))', border: '1px solid currentColor', borderRadius: 999, padding: '4px 10px', marginBottom: 18 },
  featMeta: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--cl-sub, var(--grey))', marginBottom: 10 },
  featH3: { fontFamily: 'var(--font-display)', fontSize: 'clamp(24px, 2.6vw, 32px)', lineHeight: 1.18, fontWeight: 700, letterSpacing: '-.015em', margin: '0 0 16px', textWrap: 'balance', color: 'var(--cl-fg, var(--fg-1))' },
  featExcerpt: { fontSize: 16, lineHeight: 1.6, color: 'var(--cl-sub, var(--grey))', margin: '0 0 22px', textWrap: 'pretty', flex: 1 },
  featArrow: { display: 'inline-flex', alignItems: 'center', fontSize: 14, fontWeight: 600, color: 'var(--cl-accent, var(--brandRed))', marginTop: 'auto' },
  featImgWrap: { borderRadius: 12, overflow: 'hidden', background: 'rgba(0,0,0,.05)', minHeight: 280, position: 'relative' },
  featImg: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' },

  /* Highlight cards */
  cardList: { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 },
  card: { margin: 0 },
  cardA: { display: 'flex', flexDirection: 'column', borderRadius: 14, border: '1px solid var(--cl-card-border, var(--border))', background: 'var(--cl-card-bg, #fff)', textDecoration: 'none', color: 'var(--cl-fg, inherit)', height: '100%', overflow: 'hidden', transition: 'transform .18s var(--ease-out), box-shadow .18s var(--ease-out), border-color .18s var(--ease-out)' },
  cardImgWrap: { position: 'relative', aspectRatio: '16 / 9', background: 'rgba(0,0,0,.05)', overflow: 'hidden' },
  cardImg: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform .35s var(--ease-out)' },
  cardBody: { display: 'flex', flexDirection: 'column', gap: 10, padding: '22px 26px 22px', flex: 1 },
  cardMeta: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--cl-sub, var(--grey))' },
  cardSrc: { fontWeight: 600, letterSpacing: '.04em' },
  cardDot: { opacity: .5 },
  cardMin: {},
  cardH3: { fontFamily: 'var(--font-display)', fontSize: 22, lineHeight: 1.25, fontWeight: 700, color: 'var(--cl-fg, var(--fg-1))', margin: '2px 0 4px', letterSpacing: '-.01em', textWrap: 'balance' },
  cardP: { fontSize: 15, lineHeight: 1.55, color: 'var(--cl-sub, var(--grey))', margin: 0, textWrap: 'pretty', flex: 1 },
  cardArrow: { display: 'inline-flex', alignItems: 'center', fontSize: 13, fontWeight: 600, color: 'var(--cl-accent, var(--brandRed))', marginTop: 8 },

  /* Books section */
  booksWrap: { background: 'var(--offWhite)', padding: '96px 32px 96px' },
  booksInner: { maxWidth: 1200, margin: '0 auto' },
  booksKicker: { fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--brandRed)', fontWeight: 700, marginBottom: 14 },
  booksH2: { fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 4vw, 44px)', lineHeight: 1.05, fontWeight: 700, letterSpacing: '-.015em', margin: '0 0 48px', textWrap: 'balance' },
  booksGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 32 },
  bookCard: { display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 16, overflow: 'hidden', border: '1px solid var(--border)' },
  bookCardForth: { background: '#fafaf7' },
  bookCoverWrap: { position: 'relative', aspectRatio: '1 / 1.4', background: 'var(--offWhite)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflow: 'hidden' },
  bookCoverForth: { background: 'linear-gradient(160deg, #f4ebe1 0%, #e9dfd1 100%)' },
  bookCoverImg: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', boxShadow: '0 18px 36px rgba(0,0,0,.18), 0 6px 12px rgba(0,0,0,.10)' },
  bookCoverPlaceholder: { width: '78%', aspectRatio: '1 / 1.4', background: '#fff', border: '1px solid rgba(0,0,0,.08)', borderRadius: 4, padding: 24, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 18px 36px rgba(0,0,0,.18), 0 6px 12px rgba(0,0,0,.10)' },
  bookCoverPlaceTitle: { fontFamily: 'var(--font-display)', fontSize: 22, lineHeight: 1.15, fontWeight: 700, color: 'var(--fg-1)', letterSpacing: '-.01em', textWrap: 'balance' },
  bookCoverPlaceSub: { fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--grey)', fontWeight: 600 },
  bookBadge: { position: 'absolute', top: 18, right: 18, fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', color: '#fff', background: 'var(--brandRed)', borderRadius: 999, padding: '5px 11px', fontWeight: 700 },
  bookBadgeForth: { background: 'var(--fg-1)' },
  bookBody: { padding: '28px 28px 32px', flex: 1, display: 'flex', flexDirection: 'column' },
  bookH3: { fontFamily: 'var(--font-display)', fontSize: 24, lineHeight: 1.2, fontWeight: 700, letterSpacing: '-.01em', margin: '0 0 6px', textWrap: 'balance' },
  bookSub: { fontSize: 13, color: 'var(--grey)', marginBottom: 14, fontStyle: 'italic' },
  bookP: { fontSize: 15, lineHeight: 1.6, color: 'var(--fg-1)', margin: '0 0 22px', textWrap: 'pretty', flex: 1 },
  bookCtas: { display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' },
  bookCtaPrimary: { display: 'inline-flex', alignItems: 'center', background: 'var(--brandRed)', color: '#fff', padding: '11px 18px', borderRadius: 999, fontSize: 14, fontWeight: 600, textDecoration: 'none', transition: 'background .18s var(--ease-out)' },
  bookCtaSecondary: { fontSize: 14, fontWeight: 600, color: 'var(--brandRed)', textDecoration: 'none' },

  /* Email signup */
  signupForm: { position: 'relative' },
  signupLabel: { display: 'block', fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--grey)', fontWeight: 600, marginBottom: 10 },
  signupRow: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 },
  signupInput: { padding: '11px 14px', border: '1px solid var(--border-strong)', borderRadius: 999, fontFamily: 'inherit', fontSize: 14, color: 'var(--fg-1)', background: '#fff', outline: 'none' },
  signupBtn: { padding: '11px 18px', background: 'var(--fg-1)', color: '#fff', border: 0, borderRadius: 999, fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  signupSent: { fontSize: 14, lineHeight: 1.55, color: 'var(--fg-1)', padding: '12px 16px', background: 'rgba(219,30,23,.08)', borderRadius: 10, borderLeft: '3px solid var(--brandRed)' },
  signupError: { fontSize: 13, lineHeight: 1.5, color: 'var(--brandRed)', marginTop: 8 },

  /* Archive link */
  archLinkWrap: { background: '#fff', padding: '64px 32px 96px' },
  archLinkInner: { maxWidth: 720, margin: '0 auto', textAlign: 'center' },
  archLinkA: { display: 'inline-flex', alignItems: 'center', gap: 12, padding: '20px 28px', border: '1px solid var(--border)', borderRadius: 14, textDecoration: 'none', color: 'var(--fg-1)', transition: 'border-color .18s var(--ease-out), transform .18s var(--ease-out)' },
  archLinkKick: { fontSize: 12, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--grey)', fontWeight: 700 },
  archLinkH: { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, letterSpacing: '-.01em', color: 'var(--brandRed)' },

  /* Archive page */
  archHeroWrap: { background: 'var(--offWhite)', padding: '72px 32px 56px', borderBottom: '1px solid var(--border)' },
  archHeroInner: { maxWidth: 880, margin: '0 auto' },
  archCrumb: { fontSize: 13, color: 'var(--brandRed)', fontWeight: 600, textDecoration: 'none', display: 'inline-block', marginBottom: 24 },
  archH1: { fontFamily: 'var(--font-display)', fontSize: 'clamp(40px, 5vw, 56px)', lineHeight: 1.05, fontWeight: 700, letterSpacing: '-.02em', margin: '0 0 16px', textWrap: 'balance' },
  archLede: { fontSize: 18, lineHeight: 1.65, color: 'var(--grey)', margin: 0, maxWidth: '58ch', textWrap: 'pretty' },

  archClusterWrap: { background: '#fff', padding: '72px 32px 16px' },
  archClusterInner: { maxWidth: 880, margin: '0 auto' },
  archGroup: { marginBottom: 44 },
  archGroupH3: { fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--fg-1)', margin: '0 0 4px', letterSpacing: '-.005em' },
  archGroupSub: { fontSize: 13, color: 'var(--grey)', fontStyle: 'italic', marginBottom: 16 },
  archList: { listStyle: 'none', padding: 0, margin: 0, borderTop: '1px solid var(--border)' },
  archItem: { borderBottom: '1px solid var(--border)' },
  archA: { display: 'flex', alignItems: 'center', gap: 16, padding: '12px 4px', textDecoration: 'none', color: 'var(--fg-1)', transition: 'background .18s var(--ease-out), padding .18s var(--ease-out)' },
  archThumb: { width: 64, height: 64, objectFit: 'cover', borderRadius: 8, flexShrink: 0, background: 'var(--offWhite)' },
  archTitle: { fontSize: 17, lineHeight: 1.4, flex: 1, textWrap: 'pretty' },
  archYear: { fontSize: 13, color: 'var(--grey)', fontStyle: 'italic' },
  archExt: { fontSize: 14, color: 'var(--brandRed)', opacity: .7, transition: 'transform .18s var(--ease-out), opacity .18s var(--ease-out)' },
};

export { AN_Highlights, AN_Archive };
